var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { buildString } from './utils/buildString';
var automationIdInjectionEnabled = true;
var defaultTestAttribute = 'data-cvent-id';
/**
 * Enables the injection of test is via the resolveTestId and injectTestId methods.
 **/
export function enableAutomationSupport() {
    automationIdInjectionEnabled = true;
}
/**
 * Disables the injection of test is via the resolveTestId and injectTestId methods.
 **/
export function disableAutomationSupport() {
    automationIdInjectionEnabled = false;
}
/**
 * Sets a default test attribute to use if one is not provided when calling
 * resolveTestId or injectTestId. Defaults to 'data-cvent-id'
 **/
export function setDefaultTestAttribute(newDefaultTestAttribute) {
    if (!newDefaultTestAttribute.startsWith('data-')) {
        throw new Error('test automation attribute must be valid data-* attribute');
    }
    defaultTestAttribute = newDefaultTestAttribute;
}
/**
 * Creates an object with a key value pair with the form { [testAttributeName]: id }.
 * The id is created from the test attribute found in props and the provided
 * postfix. If the test attribute is not present then an empty object is
 * returned. The resulting object can be spread into a dom element to be used
 * by tools such as XPath to locate a specific html element.
 *
 * This is a legacy method maintained for backwards compatibility. Ideally the
 * testId should be known and not pulled out by this method. Please see the
 * readme for more information.
 **/
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function resolveTestId(props, postFix, testAttributeName) {
    var _a;
    if (postFix === void 0) { postFix = ''; }
    if (testAttributeName === void 0) { testAttributeName = defaultTestAttribute; }
    var attributePresent = props &&
        props[testAttributeName] !== null &&
        props[testAttributeName] !== undefined &&
        props[testAttributeName] !== '';
    return automationIdInjectionEnabled && attributePresent
        ? (_a = {},
            _a[testAttributeName] = "" + (props ? props[testAttributeName] : '') + postFix,
            _a) : {};
}
/**
 * Creates an object with a key value pair with the form { [testAttributeName]: testId }.
 * If the testId is empty then an empty object is returned. The resulting object
 * can be spread into a dom element to be used by tools such as XPath to
 * locate a specific html element.
 *
 * In some cases this method may be used to create an ID which is then spread
 * into a custom react component to then later be picked up by the
 * resolveTestId method. Note that this is not the preferred approach any more
 * and simply passing a TestId to a component is preferred. Please see the
 * README for more information.
 *
 * @param {string} testId - The id used as the value for the data attribute.
 * @param {string} testAttributeName - The key defining the data attribute.
 */
export var injectTestId = function (testId, testAttributeName) {
    var _a;
    if (testAttributeName === void 0) { testAttributeName = defaultTestAttribute; }
    return automationIdInjectionEnabled && testId
        ? (_a = {}, _a[testAttributeName] = testId, _a)
        : {};
};
/**
 * The following utils extend upon injectTestId concepts
 * providing the additional functionality
 * - concat an array of ID's with predetermined separators
 * - sanitize each ID with preset replace behaviors
 * This also exports separate functions for handling `testID` and `data-cvent-id`
 */
/**
 * Presets
 */
var DEFAULT_ID_SEPARATOR = '__';
var DEFAULT_SANITIZE_SEPARATOR = '-';
var DEFAULT_SANITIZE_REGEXP = /\s+/g;
var DEFAULT_BUILD_STRING_OPTIONS = {
    idSeparator: DEFAULT_ID_SEPARATOR,
    sanitizeSeparator: DEFAULT_SANITIZE_SEPARATOR,
    sanitizeRegExp: DEFAULT_SANITIZE_REGEXP
};
/**
 * buildProp
 *
 * This is primarily an internal function. Given an array of ids and options, return an object with configured propName
 * key and sanitized concated id value. Returns undefined if the first id is falsey
 * or automationIdInjectionEnabled is false.
 *
 * @param {string[]} ids - Array of ids to sanitize and concatenate
 * @param {object} options - Prop name and string sanitization configuration options
 * @returns {(object|undefined)} Spreadable object with configured propName key, sanitized concatenated id value
 */
export var buildProp = function (ids, options) {
    var _a;
    var _b;
    var _c = options.propName, propName = _c === void 0 ? defaultTestAttribute : _c, buildStringOptions = __rest(options, ["propName"]);
    return automationIdInjectionEnabled && ((_b = ids[0]) === null || _b === void 0 ? void 0 : _b.length)
        ? (_a = {},
            _a[propName] = buildString(ids, __assign(__assign({}, DEFAULT_BUILD_STRING_OPTIONS), buildStringOptions)),
            _a) : undefined;
};
/**
 * buildPropFactory
 *
 * This is primarily an internal function but can be used to generate custom testID behaviors.
 * Returns a curried buildIdProp function with custom configurations. NOTE: Providing a propName
 * option will override global `defaultTestAttribute` inheritance.
 *
 * @param {object} options - Prop name and string sanitization configuration options
 * @returns {function} buildIdProp function with curried preset options
 */
export var buildIdPropFactory = function (presetOptions) { return function (ids, userOptions) {
    if (ids === void 0) { ids = []; }
    return buildProp(ids, __assign(__assign({}, presetOptions), userOptions));
}; };
/** buildDataCventIdProp
 *
 * A drop-in replacement for injectTestId. Provides same functonality with additional features:
 * - concat an array of ID's with predetermined separators
 * - sanitize each ID with preset replace behaviors
 * Respects `automationIdInjectionEnabled` flag and `defaultTestAttribute` setting defaulting to `data-cvent-id`.
 * Options allow overrides of sanitization and seperator defaults.
 *
 * @param {string[]} ids - Array of ids to sanitize and concatenate
 * @param {object} options - String sanitization configuration options
 * @returns {(object|undefined)} Spreadable object with configured propName key, sanitized concatenated id value
 */
export var buildDataCventIdProp = buildIdPropFactory();
/** buildTestIdProp
 *
 * Similar to buildDataCventIdProp, however returns object with `testID` key, useful for composing
 * testID's in compound components. Respects `automationIdInjectionEnabled` flag. Does not respect to
 * `defaultTestAttribute` setting as this is a react prop only and shouldn't change.
 *
 * @param {string[]} ids - Array of ids to sanitize and concatenate
 * @param {object} options - String sanitization configuration options
 * @returns {(object|undefined)} Spreadable object with `testID` propName key, sanitized concatenated id value
 */
export var buildTestIdProp = buildIdPropFactory({ propName: 'testID' });
